"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { type Message } from "@/components/chat";
import { type AudioMode, type CallStatus, NordWebSocket } from "@/lib/ws";
import { useAudioStreamQueue } from "./useAudioStreamQueue";
import { useAlwaysOnMic } from "./useAlwaysOnMic";

export function useWebSocketSession(audioMode: AudioMode = "file") {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [ended, setEnded] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const wsRef = useRef<NordWebSocket | null>(null);
  const { enqueue, resetQueue, waitForDrain } = useAudioStreamQueue();
  const playbackGenRef = useRef(0);

  const sendChunk = useCallback((pcm: ArrayBuffer) => {
    wsRef.current?.sendAudio(pcm);
  }, []);

  const { start: startMic, stop: stopMic } = useAlwaysOnMic(sendChunk);

  function handleSessionStart(id: string, greeting: string) {
    wsRef.current?.close();
    wsRef.current = null;
    stopMic();
    resetQueue();

    setSessionId(id);
    setMessages([{ role: "assistant", content: greeting }]);
    setEnded(false);
    setCallStatus("idle");

    const ws = new NordWebSocket(id, {
      onOpen() {
        setCallStatus("listening");
        startMic();
      },
      onVadState(speaking) {
        setCallStatus(speaking ? "user_speaking" : "processing");
      },
      onTranscription(text) {
        resetQueue();
        setMessages((prev) => [
          ...prev,
          { role: "user", content: text },
          { role: "assistant", content: "" },
        ]);
      },
      onDelta(token) {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          updated[updated.length - 1] = { ...last, content: last.content + token };
          return updated;
        });
      },
      onReply(data) {
        const { reply, ended: turnEnded, ...serverDebug } = data;
        setEnded(turnEnded as boolean);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: reply as string,
            debug: { ended: turnEnded, ...serverDebug },
          };
          return updated;
        });
        const gen = ++playbackGenRef.current;
        waitForDrain().then(() => {
          if (playbackGenRef.current === gen) {
            wsRef.current?.sendMessage({ type: "playback_done" });
          }
        });
      },
      onAudioChunk(wav) {
        setCallStatus("assistant_speaking");
        enqueue(wav);
      },
      onTurnComplete() {
        setCallStatus("listening");
      },
      onStopPlayback() {
        playbackGenRef.current++;
        resetQueue();
        setCallStatus("processing");
      },
      onError(msg) {
        console.error("[ws]", msg);
        toast.error(msg);
      },
      onClose() {
        wsRef.current = null;
        stopMic();
        setCallStatus("idle");
      },
    }, audioMode);
    wsRef.current = ws;
  }

  const hangUp = useCallback(() => {
    stopMic();
    wsRef.current?.close();
    wsRef.current = null;
    setCallStatus("idle");
  }, [stopMic]);

  useEffect(() => {
    return () => {
      stopMic();
      wsRef.current?.close();
    };
  }, [stopMic]);

  return { sessionId, messages, ended, callStatus, handleSessionStart, hangUp };
}
